import React, { useContext, useRef, useState } from 'react';
import CommonTable from 'components/CommonTable';
import { Card } from 'components/CommonWidgets';
import styled from 'styled-components';
import { CustomSelect } from 'components/CustomForm';
import { Doughnut } from 'react-chartjs-2';
import { device } from 'helpers/screen';
import { getStoreReport } from 'network/apis/ReportingApi';
import { useQuery } from 'react-query';
import { getStringColor } from 'helpers/strings';
import Skeleton from 'react-loading-skeleton';
import { DefaultInputFilter, DefaultConcentrateFilter, DefaultProvinceFilter } from 'redux/global.consts';
import { getProvinces } from 'network/apis/InformationApi';
import { getInputs } from 'network/apis/CommonApi';
import {
	buildFiltersQuery,
	handleInputChange,
	handleProvinceChange,
} from 'admin-app/redux/dashboard-filter.slice';
import { DashboardContext } from 'context/DashboardContext';
import useScreen from 'hooks/useScreen';
import { useAdminSelector, useAdminDispatch } from 'admin-app/redux/admin.store';

const barOptions = {
	plugins: {
		title: {
			display: false,
			text: ''
		},
		legend: {
			position: 'right'
		}
	},
	responsive: false,
	maintainAspectRatio: false,
};

const getChartData = (stores) => {
	const concentrate = stores?.data?.concentrate;
	const inputs = stores?.data?.inputs ?? [];
	const items = [concentrate, ...inputs];

	return {
		labels: items?.map((item) => item?.name?.name || item?.name || '-') ?? [],
		datasets: [
			{
				label: '# of Votes',
				data: items?.map((item) => item?.sum) ?? [],
				backgroundColor: items?.map(
					(item) => getStringColor(item?.name?.name || item?.name || '-')
				) ?? [],
			}
		],
	};
};

const ChartWrapper = styled.div`
	position: relative;

	@media ${device.mobileS} {
		padding: 6.5% 0 6.5% 0 !important;
		transform: scale(0.3) !important;
	}
	@media ${device.mobileM} {
		padding: 6.5% 0 6.5% 0 !important;
		transform: scale(0.5) !important;
	}
	@media ${device.mobileL} {
		padding: 6.5% 0 6.5% 0 !important;
		transform: scale(0.7) !important;
	}
	@media ${device.tablet} {
		padding: 6.5% 0 6.5% 18% !important;
		transform: scale(1) !important;
	}
	@media ${device.laptopSM} {
		padding: 6.5% 0 6.5% 18%;
		transform: scale(1.4);
	}
	@media ${device.laptop} {
		padding: 6.5% 0 6.5% 18%;
		transform: scale(1.4);
	}
	@media ${device.laptopL} {
		padding: 6.5% 0 6.5% 22%;
		transform: scale(1.6);
	}
	@media (min-width: 1600px) {
		padding: 6.5% 0 6.5% 26%;
		transform: scale(2);
	}
	@media ${device.desktop} {
		padding: 6.5% 0 6.5% 28%;
		transform: scale(2.2);
	}
`;

const Holder = styled.div`
	background-color: white;
	border-bottom-left-radius: 26px;
	border-bottom-right-radius: 26px;
	width: 100%;
	margin: 0;
	padding: 1rem;

	& > div.report-card, & > div.chart-card {
		margin: 0.8rem 1.5rem;
	}
`;

const FilterRow = styled.div`
	display: flex;
	flex-direction: ${(props) => props.ismobile ? 'column-reverse' : 'row'};
	justify-content: space-between;
	align-items: center;
	padding: ${(props) => props.ismobile ? '0' : '0 3rem 0 5rem'};

	& > div.numbers-grid {
		display: grid;
		grid-template-rows: 1fr 1fr;
		row-gap: 0.8rem;
		margin-top: ${(props) => props.ismobile ? '1.5rem' : '0'};

		& > div {
			display: grid;
			grid-template-columns: 1fr 1fr;
			text-align: center;

			span {
				white-space: nowrap;
				overflow: hidden;
				text-overflow: ellipsis;
			}
		}
	}

	& > div.selects-row {
		width: 100;
		flex-shrink: 1;		
		display: flex;
		justify-content: space-around;
		align-items: center;
	}
`;

const StoreTab = () => {
	const chartRef = useRef();
	const { currentTab } = useContext(DashboardContext);
	const dispatch = useAdminDispatch();
	const filterState = useAdminSelector((store) => store.dashboardFilter);

	const { lessThan768 } = useScreen();

	const { data: stores, isLoading } = useQuery(
		['get-stores-report', filterState.storesReportFilter],
		() => getStoreReport(filterState.storesReportFilter),
		{
			refetchOnWindowFocus: false,
			refetchInterval: 10000,
			enabled: currentTab === 'store',
		}
	);

	const getTotalCount = () => `${stores?.data?.statistics?.count ?? 0}`.persianCurrency();
	const getSum = () => `${(stores?.data?.concentrate?.sum ?? 0) + (stores?.data?.statistics?.sum ?? 0)}`.persianCurrency();
	const getUnitText = () => `:مجموع(${stores?.data?.unit?.name})`;

	const { data: inputsData } = useQuery(
		["get-inputs"],
		() => getInputs(),
		{
			refetchOnWindowFocus: false,
			refetchInterval: 10000,
			enabled: currentTab === 'store',
		}
	);
	const getInputOtions = () => {
		if (inputsData?.data) {
			const inputOptions = inputsData.data.map((input, index) => {
				return { value: input?.item_id, label: input?.name };
			});

			return [DefaultInputFilter, DefaultConcentrateFilter, ...inputOptions];
		}

		return [];
	};

	const { data: provinceData } = useQuery(
		["get-provinces"],
		() => getProvinces(),
		{
			refetchOnWindowFocus: false,
			enabled: currentTab === 'store',
		}
	);
	const getProvinceOptions = () => {
		if (provinceData?.data) {
			const provinceOptions = provinceData.data.map((province, index) => {
				return { value: province?.province_id, label: province?.name };
			});

			return [DefaultProvinceFilter, ...provinceOptions];
		}

		return [];
	};

	const onInputChange = (newValue) => {
		dispatch(handleInputChange(newValue));
		dispatch(buildFiltersQuery());
	}

	const onProvinceChange = (newValue) => {
		dispatch(handleProvinceChange(newValue));
		dispatch(buildFiltersQuery());
	}

	return (
		<Holder>
			<div className="text-end w-100">گزارش در لحظه</div>
			<div className="report-card">
				<Card>
					<FilterRow ismobile={lessThan768}>
						{isLoading ? (
							<Skeleton count={2} width={150} height={20} />
						) : (
							<div className="numbers-grid">
								<div>
									<span>{getTotalCount()}</span>
									<span>:انواع ترکیب ها</span>
								</div>
								<div>
									<span>{getSum()}</span>
									<span>{getUnitText()}</span>
								</div>
							</div>
						)}
						<div className='selects-row'>
							<CustomSelect
								placeholder='همه نهاده ها'
								className="mx-2"
								radius="14"
								options={getInputOtions()}
								onChange={onInputChange}
								value={filterState.selectedInput}
								menuPosition='fixed'
							/>
							<CustomSelect
								placeholder='همه استان ها'
								className="mx-2"
								radius="14"
								options={getProvinceOptions()}
								onChange={onProvinceChange}
								value={filterState.selectedProvince}
								menuPosition='fixed'
							/>
						</div>
					</FilterRow>
				</Card>
			</div>
			<div className='chart-card'>
				<Card>
					{isLoading ? (
						<Skeleton count={1} height={280} />
					) : (
						<ChartWrapper>
							<Doughnut data={getChartData(stores)} options={barOptions} ref={chartRef} />
						</ChartWrapper>
					)}
				</Card>
			</div>
		</Holder>
	);
};

export default StoreTab;
